import { DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { TreatmentResponse } from '../../../../models/api/view/claim/details/medical/treatment-response';
import { ExportService } from '../../../../services/export/export.service';
import { ProviderExportMapper } from '../../../../utilities/provider-export-mapper';
import { ReferralExportMapper } from '../../../../utilities/referral-export-mapper';
import { TreatmentExportMapper } from '../../../../utilities/treatment-export-mapper';
import { BaseComponent } from '../../../base/base.component';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-treatment',
  imports: [
    RouterModule,
    TooltipModule,
    CardComponent,
    TableComponent,
    ColumnComponent,
    DetailPanelComponent,
    DetailItemComponent,
    LoadingPanelComponent,
  ],
  providers: [DatePipe],
  template: `
    <app-loading-panel
      loadingStyle="Box"
      [loadingHeight]="500"
      [state]="loading ? 'Loading' : 'Loaded'"
      [error]="error"
    >
      <ng-template #loaded>
        <div class="tw-flex tw-flex-col tw-gap-8 tw-pt-4">
          <div class="tw-flex">
            <app-detail-panel [dataSource]="treatment" class="tw-basis-2/5">
              <app-detail-item
                label="Initial treatment"
                name="initialTreatment"
              />
              <app-detail-item
                label="Doctor's first report"
                name="doctorFirstReport"
                format="b"
              />
            </app-detail-panel>
          </div>

          <app-card label="Treatment plans">
            <app-table
              [dataSource]="treatment?.treatmentPlans || []"
              [pageSize]="5"
              recordLabel="Treatment plan"
              [showExport]="true"
              (export)="onExportTreatmentPlans($event)"
            >
              <app-column
                label="Starting date"
                name="startingDate"
                format="d"
              />
              <app-column label="Ending date" name="endingDate" format="d" />
              <app-column label="Code" name="code" />
              <app-column label="Description" name="description" />
              <app-column
                label="Units requested"
                name="unitsRequested"
                format="n"
              />
              <app-column
                label="Units approved"
                name="unitsApproved"
                format="n"
              />
            </app-table>
          </app-card>

          <app-card label="Referrals">
            <app-table
              [dataSource]="treatment?.referrals || []"
              [pageSize]="5"
              recordLabel="Referral"
              [showExport]="true"
              (export)="onExportReferrals($event)"
            >
              <app-column label="Date open" name="dateOpen" format="d" />
              <app-column label="Referred by" name="referredBy" />
              <app-column label="Type" name="type" />
              <app-column label="Status" name="status" />
              <app-column label="Rejected by" name="rejectedBy" />
              <app-column label="Date closed" name="dateClosed" format="d" />
            </app-table>
          </app-card>

          <div class="tw-flex tw-gap-8">
            <app-card label="Length of Stay (days)" class="tw-basis-1/3">
              <app-detail-panel
                [dataSource]="treatment?.lengthOfStay"
                class="tw-basis-2/5"
              >
                <app-detail-item
                  label="Requested"
                  name="requested"
                  format="n"
                />
                <app-detail-item label="Approved" name="approved" format="n" />
              </app-detail-panel>
            </app-card>

            <app-card label="Hospitalization" class="tw-basis-2/3">
              <div class="tw-flex tw-gap-8">
                <app-detail-panel
                  [dataSource]="treatment?.hospitalization"
                  class="tw-basis-1/2"
                >
                  <app-detail-item
                    label="Admitted"
                    name="admitted"
                    format="d"
                  />
                  <app-detail-item
                    label="Released"
                    name="released"
                    format="d"
                  />
                  <app-detail-item
                    label="Days saved"
                    name="daysSaved"
                    format="n"
                  />
                  <app-detail-item label="In/Out patient" name="inOutPatient" />
                </app-detail-panel>
                <app-detail-panel
                  [dataSource]="treatment?.hospitalization"
                  class="tw-basis-1/2"
                >
                  <app-detail-item
                    label="Hospitalized"
                    name="hospitalized"
                    format="b"
                  />
                  <app-detail-item
                    label="Hospitalized date"
                    name="hospitalizedDate"
                    format="d"
                  />
                  <app-detail-item label="Surgery" name="surgery" format="b" />
                  <app-detail-item
                    label="Surgery date"
                    name="surgeryDate"
                    format="d"
                  />
                </app-detail-panel>
              </div>
            </app-card>
          </div>

          <app-card label="Providers">
            <app-table
              [dataSource]="treatment?.providers || []"
              [pageSize]="5"
              recordLabel="Provider"
              [showExport]="true"
              (export)="onExportProviders($event)"
            >
              <app-column name="type" label="Type" />
              <app-column name="providerFacility" label="Provider/Facility" />
              <app-column name="address" label="Address" />
              <app-column name="city" label="City" />
              <app-column name="state" label="State" />
              <app-column name="zipCode" label="Zip code" />
            </app-table>
          </app-card>
        </div>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class TreatmentComponent extends BaseComponent implements OnInit {
  private exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  currentClaim: ClaimRow;
  treatment: TreatmentResponse | null = null;

  loading = false;
  error: any = null;

  @HandleLoadingState()
  async ngOnInit(): Promise<void> {
    this.currentClaim = this.view.currentClaim();

    // Fetch treatment data from API
    this.treatment = await this.api.medical.getTreatment({
      recordId: this.currentClaim.recordId,
      recordSource: this.currentClaim.recordSource,
    });
  }

  async onExportTreatmentPlans(format: ExportFormat) {
    const rows = this.treatment?.treatmentPlans || [];
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `TreatmentPlans_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = TreatmentExportMapper.toCsvExport(
        rows,
        'Treatment Plans',
      );
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = TreatmentExportMapper.toExcelExport(
        rows,
        'Treatment Plans',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }

  async onExportReferrals(format: ExportFormat) {
    const rows = this.treatment?.referrals || [];
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `Referrals_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = ReferralExportMapper.toCsvExport(rows, 'Referrals');
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = ReferralExportMapper.toExcelExport(
        rows,
        'Referrals',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }

  async onExportProviders(format: ExportFormat) {
    const rows = this.treatment?.providers || [];
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `Providers_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = ProviderExportMapper.toCsvExport(rows, 'Providers');
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = ProviderExportMapper.toExcelExport(
        rows,
        'Providers',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }
}
